/** Strings of `/profile` and its cards: `profileEn` is the source, `profileCs` must have the same keys. */
export const profileEn = {
  prof: {
    title: "Profile",
    meeting: {
      title: "Meeting identity",
      description:
        "How a recorded meeting knows a participant is you — and so whether it proposes your time at all on <link>Time suggestions</link>. Discord matches on your account, not your name. Google Meet knows you only by email, so if you join on a different address than your Hub sign-in, add it below.",
    },
    tokens: {
      title: "API tokens",
      description:
        "Bearer credentials for your own machines — an automatic time tracker pushing proposed time entries into Hub, for instance. They aren't a login: each token reaches only the endpoints its scopes allow. Send one as <code>Authorization: Bearer …</code>. Proposals show up on <link>Time suggestions</link>.",
      loadFailed: "Couldn't load your API tokens.",
      createFailed: "Couldn't create the token",
      revoked: "Token revoked",
      revokeFailed: "Couldn't revoke the token",
      scopesUpdated: "Token scopes updated",
      updateFailed: "Couldn't update the token",
      copyNow: "Copy this token now — it won't be shown again.",
      hashOnly:
        "Hub stores only a hash of it, so there's no way to look it up later. Lost it? Revoke the token and create another.",
      done: "Done",
      saving: "Saving",
      save: "Save",
      cancel: "Cancel",
      lastUsed: "last used ",
      neverUsed: "never used",
      expires: "· expires ",
      editScopes: "Edit scopes",
      revoke: "Revoke",
      none: "No tokens yet.",
      purpose: "What is it for?",
      namePlaceholder: "MacBook time tracker",
      mayDo: "What may it do?",
      noScopes: "No scopes available.",
      creating: "Creating",
      create: "Create token",
    },
    password: {
      title: "Password",
      saved: "Password saved",
      descriptionSet:
        "You can sign in with your e-mail and password. Change it here; if you forget it, sign in with a link from e-mail and set a new one.",
      descriptionUnset:
        "Set a password to sign in without waiting for an e-mail. The sign-in link keeps working too.",
      current: "Current password",
      currentHint: "Not needed right after signing in with a link from e-mail.",
      newPassword: "New password",
      password: "Password",
      minLength: "At least {{count}} characters.",
      repeat: "Repeat it",
      mismatch: "The two don't match.",
      saving: "Saving",
      change: "Change password",
      set: "Set password",
    },
    timer: {
      title: "Timer in Chrome",
      description:
        "A toolbar button that starts and stops your Hub timer from any tab and shows how long it has run. It uses your Hub sign-in in this browser, nothing to paste.",
      download: "Download the extension",
      step1: "Unzip it into a folder you'll keep.",
      step2: "Open <code>chrome://extensions</code> and turn on Developer mode.",
      step3: "Click Load unpacked and pick that folder.",
      step4: "Pin Netlion Hub Timer from the puzzle menu.",
      update:
        "To update, download it again, unzip over the same folder and click the reload arrow on the extension.",
    },
    whatsapp: {
      loadFailed: "Couldn't load your WhatsApp link.",
      codeFailed: "Couldn't make a code.",
      unlinkFailed: "Couldn't unlink.",
      title: "Radim on WhatsApp",
      description:
        "Plan your projects with Radim in a WhatsApp chat, typed, as voice notes or on a call. He moves between projects as you talk, and everything he changes can be put back.",
      linked: "Your WhatsApp is linked. Write or call <num/> and just say what you need.",
      radim: "Radim",
      unlink: "Unlink",
      send: "Send <code>{{code}}</code> to <num/> from your WhatsApp within 30 minutes. Then reload this page.",
      radimsNumber: "Radim's number",
      link: "Link my WhatsApp",
    },
  },
};

export const profileCs: typeof profileEn = {
  prof: {
    title: "Profil",
    meeting: {
      title: "Identita na schůzkách",
      description:
        "Podle čeho nahraná schůzka pozná, že účastník jste vy — a tedy zda vám vůbec navrhne čas v <link>Návrzích času</link>. Discord páruje podle vašeho účtu, ne podle jména. Google Meet vás zná jen podle e-mailu, takže pokud se připojujete z jiné adresy, než jakou se přihlašujete do Hubu, přidejte ji níže.",
    },
    tokens: {
      title: "API tokeny",
      description:
        "Přístupové údaje pro vaše vlastní stroje — například automatický tracker času, který do Hubu posílá navržené záznamy času. Nejsou to přihlášení: každý token dosáhne jen na endpointy, které povolují jeho oprávnění. Posílá se jako <code>Authorization: Bearer …</code>. Návrhy se objeví v <link>Návrzích času</link>.",
      loadFailed: "API tokeny se nepodařilo načíst.",
      createFailed: "Token se nepodařilo vytvořit",
      revoked: "Token byl zneplatněn",
      revokeFailed: "Token se nepodařilo zneplatnit",
      scopesUpdated: "Oprávnění tokenu byla změněna",
      updateFailed: "Token se nepodařilo upravit",
      copyNow: "Zkopírujte si token hned — už se znovu nezobrazí.",
      hashOnly:
        "Hub si uchovává jen jeho hash, takže ho později nelze dohledat. Ztratili jste ho? Zneplatněte token a vytvořte nový.",
      done: "Hotovo",
      saving: "Ukládám",
      save: "Uložit",
      cancel: "Zrušit",
      lastUsed: "naposledy použit ",
      neverUsed: "nikdy nepoužit",
      expires: "· vyprší ",
      editScopes: "Upravit oprávnění",
      revoke: "Zneplatnit",
      none: "Zatím žádné tokeny.",
      purpose: "K čemu slouží?",
      namePlaceholder: "Tracker času na MacBooku",
      mayDo: "Co smí dělat?",
      noScopes: "Žádná oprávnění k dispozici.",
      creating: "Vytvářím",
      create: "Vytvořit token",
    },
    password: {
      title: "Heslo",
      saved: "Heslo uloženo",
      descriptionSet:
        "Můžete se přihlásit e-mailem a heslem. Změníte ho tady; pokud ho zapomenete, přihlaste se odkazem z e-mailu a nastavte si nové.",
      descriptionUnset:
        "Nastavte si heslo, abyste se mohli přihlásit bez čekání na e-mail. Přihlašovací odkaz funguje dál.",
      current: "Současné heslo",
      currentHint: "Hned po přihlášení odkazem z e-mailu ho zadávat nemusíte.",
      newPassword: "Nové heslo",
      password: "Heslo",
      minLength: "Alespoň {{count}} znaků.",
      repeat: "Zopakujte ho",
      mismatch: "Hesla se neshodují.",
      saving: "Ukládám",
      change: "Změnit heslo",
      set: "Nastavit heslo",
    },
    timer: {
      title: "Časovač v Chrome",
      description:
        "Tlačítko na liště, které z jakékoli záložky spouští a zastavuje váš časovač v Hubu a ukazuje, jak dlouho běží. Používá vaše přihlášení do Hubu v tomto prohlížeči, nic nevkládáte.",
      download: "Stáhnout rozšíření",
      step1: "Rozbalte ho do složky, kterou si ponecháte.",
      step2: "Otevřete <code>chrome://extensions</code> a zapněte Režim pro vývojáře.",
      step3: "Klikněte na Načíst rozbalené a vyberte tu složku.",
      step4: "Připněte Netlion Hub Timer z nabídky s puzzle.",
      update:
        "Pro aktualizaci rozšíření stáhněte znovu, rozbalte přes stejnou složku a klikněte na šipku načíst znovu u rozšíření.",
    },
    whatsapp: {
      loadFailed: "Propojení s WhatsAppem se nepodařilo načíst.",
      codeFailed: "Kód se nepodařilo vytvořit.",
      unlinkFailed: "Odpojení se nepodařilo.",
      title: "Radim na WhatsAppu",
      description:
        "Plánujte své projekty s Radimem v chatu na WhatsAppu — psaně, hlasovými zprávami nebo hovorem. Pohybuje se mezi projekty podle toho, jak mluvíte, a vše, co změní, jde vrátit zpět.",
      linked:
        "Váš WhatsApp je propojený. Napište nebo zavolejte na <num/> a řekněte, co potřebujete.",
      radim: "Radim",
      unlink: "Odpojit",
      send: "Pošlete <code>{{code}}</code> ze svého WhatsAppu na <num/> do 30 minut. Pak stránku znovu načtěte.",
      radimsNumber: "číslo Radima",
      link: "Propojit můj WhatsApp",
    },
  },
};
